import { useEffect, useMemo, useRef, useState } from "react";
import { prefersReducedMotion } from "@/lib/anime/prefersReducedMotion";

/**
 * HeroToContentWarp · scroll-pinned section that provides the runway
 * for the warp dolly. The actual 3D scene lives in HeroGlobeStage
 * (parent), which is sticky across hero + warp so the same globe
 * persists. This section only contributes:
 *  · 200vh of scroll runway (so the camera has space to dolly through)
 *  · stage copy reveals (animejs.com-style staged text)
 *
 * Desktop only · the effect needs scroll runway and steady framerate.
 */
export function HeroToContentWarp() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState(0);
  const reduced = useMemo(() => prefersReducedMotion(), []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    let raf = 0;
    const update = () => {
      const rect = wrap.getBoundingClientRect();
      const h = window.innerHeight;
      const span = Math.max(1, rect.height - h);
      const passed = -rect.top;
      const s = Math.max(0, Math.min(1, passed / span));
      // Stage thresholds are tuned to land each piece of copy at a
      // different point in the spiral-dive so the reveals feel choreographed
      // with the camera, not just a fade list.
      //   0..0.15 = quiet open
      //   0.15..0.4 = stage 1 (left, approach starts)
      //   0.4..0.72 = stage 2 (centered, just before puncture)
      //   0.72..1   = stage 3 (right, post-puncture)
      const next = s < 0.15 ? 0 : s < 0.4 ? 1 : s < 0.72 ? 2 : 3;
      setStage((prev) => (prev === next ? prev : next));
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", update);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  if (reduced) return null;

  return (
    <section
      ref={wrapRef}
      className="hidden md:block relative w-full"
      style={{ height: "200vh" }}
      aria-hidden="true"
    >
      <div className="sticky top-0 w-full h-svh overflow-hidden pointer-events-none">
        {/* Stage copy · each piece lands at a different point on screen
         * and slides in from a different axis, so the camera spiral and
         * the text feel choreographed instead of one centered fade list.
         * Globe behind this is rendered by HeroGlobeStage (sticky parent). */}
        <div className="pointer-events-none absolute inset-0 px-6 md:px-16 lg:px-28">
          {/* Stage 1 · top-left, slides in from the LEFT as the camera
              starts its descent toward the globe. */}
          <p
            className="absolute top-[18%] left-[6%] md:left-[8%] max-w-[20ch] font-mono text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-primary/80 transition-all ease-out [transition-duration:900ms]"
            style={{
              opacity: stage >= 1 ? 1 : 0,
              transform: `translateX(${stage >= 1 ? 0 : -44}px)`,
            }}
          >
            past the surface
          </p>

          {/* Stage 2 · centered, scales up at the moment of puncture.
              This is the headline beat · everything else orbits around it. */}
          <h2
            className="absolute top-1/2 left-1/2 font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-foreground max-w-3xl text-center leading-[1.02] tracking-tight transition-all ease-out [transition-duration:900ms]"
            style={{
              opacity: stage >= 2 ? 1 : 0,
              transform: `translate(-50%, -50%) scale(${stage >= 2 ? 1 : 0.9})`,
              filter: `blur(${stage >= 2 ? 0 : 6}px)`,
            }}
          >
            Into the <span className="italic text-primary">trust layer</span>.
          </h2>

          {/* Stage 3 · bottom-right, slides in from the RIGHT once the
              camera has punched through and we're "inside" the network. */}
          <p
            className="absolute bottom-[18%] right-[6%] md:right-[8%] max-w-[24ch] font-mono text-[11px] uppercase tracking-[0.22em] text-primary text-right transition-all ease-out [transition-duration:900ms]"
            style={{
              opacity: stage >= 3 ? 1 : 0,
              transform: `translateX(${stage >= 3 ? 0 : 44}px)`,
            }}
          >
            you're inside the network now ↓
          </p>
        </div>
      </div>
    </section>
  );
}

export default HeroToContentWarp;
